import React from 'react';
import { Helmet } from 'react-helmet';
import { Trans, useLingui } from '@lingui/react/macro';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import {
  PET_TYPE_FOR_SEO_SLUG,
  SERVICE_TYPE_FOR_SEO_SLUG,
} from '@rover/constants/src/search/seo.constants';
import { Box, Button, Display } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import LandingHero, {
  HeroImageSrcSet,
} from '@rover/react-lib/src/pages/landing/components/LandingHero';
import LandingSearchResultsCarousel from '@rover/react-lib/src/pages/landing/components/LandingSearchResultsCarousel';
import QuickFact, {
  QuickFactHighlight,
} from '@rover/react-lib/src/pages/landing/SeoLocation/components/QuickFact';
import ServiceBenefits, {
  ServiceBenefitsImageSrcSet,
} from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceBenefits';
import {
  quickFactStickerCat,
  quickFactStickerChihuahua,
  quickFactStickerPug,
} from '@rover/react-lib/src/pages/landing/ServiceLanding/pages/shared-images';
import { ServiceBenefitsFindSitterClick } from '@rover/react-lib/src/pages/landing/utilities/analyticsEvents';
import {
  ROVER_DATA_CITATION,
  Superscript,
} from '@rover/react-lib/src/pages/shared/FooterLinks/FactCitations';
import { ApiFrontendInternalLinkingCityRetrieve200Results } from '@rover/rsdk/src/apiClient/latest/model/apiFrontendInternalLinkingCityRetrieve200Results';
import { ApiFrontendInternalLinkingNeighborhoodRetrieve200Results } from '@rover/rsdk/src/apiClient/latest/model/apiFrontendInternalLinkingNeighborhoodRetrieve200Results';
import { ApiFrontendInternalLinkingRegionRetrieve200Results } from '@rover/rsdk/src/apiClient/latest/model/apiFrontendInternalLinkingRegionRetrieve200Results';
import { OptimizedSearchResponse } from '@rover/rsdk/src/apiClient/latest/model/optimizedSearchResponse';
import { ServiceTypeSlugEnum } from '@rover/rsdk/src/apiClient/latest/model/serviceTypeSlugEnum';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import useCountryConfigurationShim from '@rover/shared/js/hooks/useCountryConfigurationShim';

import internalLinkingHero from './internal-linking-hero.jpg';
import internalLinkingServiceBenefits from './internal-linking-service-benefits.jpg';
import InternalLinkingFooter from './InternalLinkingFooter';
import NotReadyToBook from './NotReadyToBook';

type SearchResultsProps = {
  boardingResults?: OptimizedSearchResponse;
  largeDogBoardingResults?: OptimizedSearchResponse;
  catDropInResults?: OptimizedSearchResponse;
  dayCareResults?: OptimizedSearchResponse;
  walkingResults?: OptimizedSearchResponse;
};

export type Props = (
  | ApiFrontendInternalLinkingRegionRetrieve200Results
  | ApiFrontendInternalLinkingCityRetrieve200Results
  | ApiFrontendInternalLinkingNeighborhoodRetrieve200Results
) &
  SearchResultsProps;

const internalLinkingHeroImageSrcSet: HeroImageSrcSet = {
  desktop: { src: internalLinkingHero, height: 335 },
  tablet: { src: internalLinkingHero, height: 335 },
  mobile: { src: internalLinkingHero, height: 320 },
};

const serviceBenefitsImageSrcSet: ServiceBenefitsImageSrcSet = {
  desktop: {
    src: internalLinkingServiceBenefits,
    width: 585,
    height: 390,
  },
  mobile: {
    src: internalLinkingServiceBenefits,
    width: 350,
    height: 277,
  },
};

type SearchCarouselSectionProps = {
  searchResponse?: OptimizedSearchResponse;
  serviceTypeSlug: ServiceTypeSlugEnum;
  heading: string;
  locale: Locale;
  countryConfig: React.ComponentProps<typeof LandingSearchResultsCarousel>['countryConfig'];
};

const SearchCarouselSection: React.FC<SearchCarouselSectionProps> = ({
  searchResponse,
  serviceTypeSlug,
  heading,
  locale,
  countryConfig,
}) => {
  if (!searchResponse || !searchResponse.results?.length) {
    return null;
  }

  const serviceType = SERVICE_TYPE_FOR_SEO_SLUG[serviceTypeSlug];
  const petType = PET_TYPE_FOR_SEO_SLUG[serviceTypeSlug];

  const searchFilters = {
    serviceType,
    petType: [petType],
    locationType: searchResponse.derivedData?.zipCode ? 'zip-code' : undefined,
    location: searchResponse.derivedData?.zipCode as string,
    centerlat: searchResponse.derivedData?.center?.[0],
    centerlng: searchResponse.derivedData?.center?.[1],
  };

  return (
    <Section my={['6x', '12x']} py={['0x', '2x']} px={['5x', '0x']}>
      <LandingSearchResultsCarousel
        searchResponse={searchResponse}
        searchFilters={searchFilters}
        locale={locale}
        heading={heading}
        headingVariant="heading"
        serviceTypeSlug={serviceTypeSlug}
        countryConfig={countryConfig}
      />
    </Section>
  );
};

const InternalLinkingPage: React.FC<Props> = ({
  metadata,
  breadcrumbs,
  cityOrNeighborhoodServices,
  citiesOrNeighborhoods,
  boardingResults,
  largeDogBoardingResults,
  catDropInResults,
  dayCareResults,
  walkingResults,
}) => {
  const { t, i18n } = useLingui();
  const { title, content, locationType } = metadata || {};
  const currentAnchor = breadcrumbs.at(-1);
  const location = currentAnchor?.title || '';
  const { locale } = useI18nContext();
  const { current: countryConfig } = useCountryConfigurationShim();

  if (!countryConfig) {
    return null;
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex" />
        <title>{title}</title>
        <meta name="description" content={content} />
      </Helmet>
      <LandingHero
        heroImageSrcSet={internalLinkingHeroImageSrcSet}
        imageProps={{
          sx: { objectPosition: ['bottom', '50% 50%'] },
        }}
        centerContent
      >
        {/* 497px and 190px to match the design specification */}
        <LandingHero.Content
          upliftedContent
          width={['100%', '497px']}
          minHeight={['auto', '190px']}
          px={['5x', '8x']}
          pb="4x"
        >
          <LandingHero.Display mt="0x" mb="0x" size="400" webProps={{ as: 'h1', responsive: true }}>
            <Trans comment="{location} is a city or region name, e.g. 'Seattle or Alabama'">
              Personalized care for your dog or cat -{' '}
              <Display
                webProps={{ as: 'span', responsive: true, display: 'inline' }}
                size="400"
                textColor="success"
              >
                {location}
              </Display>
            </Trans>
          </LandingHero.Display>
          <LandingHero.TrustPilot justifyContent="left" />
        </LandingHero.Content>
      </LandingHero>
      <SearchCarouselSection
        searchResponse={boardingResults}
        serviceTypeSlug={ServiceTypeSlugEnum['dog-boarding']}
        heading={t`Highly-rated overnight sitters - ${location}`}
        locale={locale}
        countryConfig={countryConfig}
      />
      <QuickFact themeColor="yellow" stickerImageSrc={quickFactStickerChihuahua}>
        <Trans>
          Over <QuickFactHighlight>4.1 million</QuickFactHighlight> dogs curled up for a sleepover
          in a sitter's home on Rover
        </Trans>
        <Superscript>1</Superscript>
      </QuickFact>
      <SearchCarouselSection
        searchResponse={catDropInResults}
        serviceTypeSlug={ServiceTypeSlugEnum['cat-sitting']}
        heading={t`Cat caregivers near you - ${location}`}
        locale={locale}
        countryConfig={countryConfig}
      />
      <ServiceBenefits
        title={<Trans>Why choose a sitter on Rover</Trans>}
        headingVariant="heading"
        imageProps={{ sx: { borderRadius: '16px', objectFit: 'cover' } }}
        columnGap="10x"
        imageWidth="585px"
        background="primary"
        serviceBenefitsImageSrcSet={serviceBenefitsImageSrcSet}
      >
        <ServiceBenefits.List>
          <ServiceBenefits.ListItem title={<Trans>Background-checked house sitters</Trans>} mb="0x">
            <Trans>
              Every sitter profile has been reviewed and approved by Rover before joining our
              community.
            </Trans>
          </ServiceBenefits.ListItem>
          <ServiceBenefits.ListItem title={<Trans>A perfect match for every pet</Trans>} mb="0x">
            <Trans>
              Read sitter profiles to find 5-star reviews and specialists who understand your pet's
              unique personality.
            </Trans>
          </ServiceBenefits.ListItem>
          <ServiceBenefits.ListItem title={<Trans>Connection before booking</Trans>} mb="0x">
            <Trans>
              Schedule a free in-person meeting to ensure a perfect fit before you ever book.
            </Trans>
          </ServiceBenefits.ListItem>
        </ServiceBenefits.List>
        <Box display="flex" justifyContent={['center', 'flex-start']}>
          <Button
            variant="primary"
            size="default"
            href="/search/"
            width="100%"
            maxWidth={['321px', '259px']}
            onPress={() => emitAnalyticsEvent(new ServiceBenefitsFindSitterClick({}))}
          >
            <Trans>Find a sitter</Trans>
          </Button>
        </Box>
      </ServiceBenefits>
      <SearchCarouselSection
        searchResponse={dayCareResults}
        serviceTypeSlug={ServiceTypeSlugEnum['doggy-day-care']}
        heading={t`Perfect for high energy dogs - ${location}`}
        locale={locale}
        countryConfig={countryConfig}
      />
      <QuickFact themeColor="blue" stickerImageSrc={quickFactStickerCat} stickerAlignment="left">
        <Trans>
          More than <QuickFactHighlight>1.6 million</QuickFactHighlight> pets enjoyed cuddles and
          care from sitters on Rover
        </Trans>
        <Superscript>1</Superscript>
      </QuickFact>
      <SearchCarouselSection
        searchResponse={walkingResults}
        serviceTypeSlug={ServiceTypeSlugEnum['dog-walking']}
        heading={t`Adventure-ready dog walkers - ${location}`}
        locale={locale}
        countryConfig={countryConfig}
      />
      <QuickFact themeColor="pink" stickerImageSrc={quickFactStickerPug}>
        <Trans>
          Over <QuickFactHighlight>980,000</QuickFactHighlight> dogs traded couch time for play time
          with Doggy Day Care on Rover
        </Trans>
        <Superscript>1</Superscript>
      </QuickFact>
      <SearchCarouselSection
        searchResponse={largeDogBoardingResults}
        serviceTypeSlug={ServiceTypeSlugEnum['dog-boarding']}
        heading={t`Large dog specialists near you - ${location}`}
        locale={locale}
        countryConfig={countryConfig}
      />
      <NotReadyToBook />
      <InternalLinkingFooter
        location={location}
        locationType={locationType}
        cityOrNeighborhoodServices={cityOrNeighborhoodServices}
        citiesOrNeighborhoods={citiesOrNeighborhoods}
        citations={[i18n._(ROVER_DATA_CITATION)]}
      />
    </>
  );
};

export default InternalLinkingPage;
